import fp from 'lodash/fp';
import React from 'react';
import { connect } from 'react-redux';
import { branch, compose, lifecycle, renderComponent, withProps } from 'recompose';
import { bindActionCreators } from 'redux';
import { routes, tabKeys } from '../../../../constants';
import { isBusinessAdmin, isExternalUser, isManager, isTeamManager, notCanceled } from '../../../../services';
import { businessSelector } from '../../../../state/modules/business';
import { industryActions } from '../../../../state/modules/industry';
import { currentUserSelector } from '../../../../state/modules/user';
import Loading from '../../loading';
import AccountSettings from './account-settings';
import { BusinessDetails, BusinessTrainingPlan, Integration, Invoices, Login, Payment, Personal, TeamMembersTrainingPlans, Teams, TeamsTrainingPlans, TeamTrainingPlan, UserManagement, UsersTrainingPlans, UserTrainingPlan } from './partials';
import { KeyType } from './partials/integration';

export const UserAccountSettings = compose(
  connect((state) => ({
    user: currentUserSelector(state),
    isFetchingUserData: state.user.isFetchingUserData,
    businessSubscription: notCanceled(state.business.subscription)
  })),
  branch(
    ({ isFetchingUserData }) => isFetchingUserData === undefined || isFetchingUserData,
    renderComponent(Loading)
  ),
  withProps(
    ({ user }) => ({
      isBusinessAdmin: isBusinessAdmin(user),
      isExternalUser: isExternalUser(user),
      isManager: isManager(user)
    })
  ),
  withProps(
    ({ isBusinessAdmin, isManager, isExternalUser, businessSubscription }) => {
      const userTabs = isExternalUser ? [] : [
        {
          path: tabKeys.accountSettings.personalDetail,
          title: 'My details',
          content: <Personal />
        },
        {
          path: tabKeys.accountSettings.loginDetail,
          title: 'Login details',
          content: <Login />
        },
        isBusinessAdmin ? {
          path: tabKeys.accountSettings.businessDetails,
          title: 'Company details',
          content: <BusinessDetails />
        } : null,
        {
          path: tabKeys.accountSettings.paymentDetail,
          title: 'Payment details',
          content: <Payment />
        },
        {
          path: tabKeys.accountSettings.invoices,
          title: 'Invoices',
          content: <Invoices />
        },
        {
          path: tabKeys.accountSettings.integration,
          title: 'Integrations',
          content: <Integration type={KeyType.Individual} />
        }
      ]
      return ({
        routes: routes.app.accountSettings,
        title: 'Account Settings',
        tabs: fp.filter(
          (tab) => tab
        )(userTabs)
      })
    }
  )
)(AccountSettings);

export const UsersAndTeamsSettings = compose(
  connect((state) => ({
    user: currentUserSelector(state),
    isFetchingUserData: state.user.isFetchingUserData
  })),
  branch(
    ({ isFetchingUserData }) => isFetchingUserData === undefined || isFetchingUserData,
    renderComponent(Loading)
  ),
  withProps(
    ({ user }) => ({
      isBusinessAdmin: isBusinessAdmin(user)
    })
  ),
  withProps(
    ({ isBusinessAdmin }) => ({
      routes: routes.app.usersAndTeamsSettings,
      title: 'Users & Teams Settings',
      tabs: fp.filter(
        (tab) => tab
      )([
        isBusinessAdmin ? {
          path: tabKeys.usersAndTeamsSettings.userManagement,
          title: 'Manage users',
          content: <UserManagement />
        } : null,
        {
          path: tabKeys.usersAndTeamsSettings.teams,
          title: 'Manage teams',
          content: <Teams />
        }
      ])
    }))
)(AccountSettings);

export const TrainingPlansSettings = compose(
  connect(
    (state) => ({
      user: currentUserSelector(state),
      business: businessSelector(state),
      isFetchingUserData: state.user.isFetchingUserData
    }),
    (dispatch) => ({
      getIndustries: bindActionCreators(industryActions.industries.industriesWithCpdCategorys, dispatch)
    })
  ),
  lifecycle({
    componentDidMount() {
      this.props.getIndustries()
    }
  }),
  branch(
    ({ isFetchingUserData }) => isFetchingUserData === undefined || isFetchingUserData,
    renderComponent(Loading)
  ),
  withProps(
    ({ user, business }) => ({
      isBusinessAdmin: isBusinessAdmin(user),
      isTeamManager: isTeamManager(user, business),
      isManager: isManager(user)
    })
  ),
  withProps(
    ({ isBusinessAdmin, isTeamManager, isManager, location }) => ({
      routes: routes.app.trainingPlansSettings,
      title: 'Training Plans Settings',
      tabs: fp.filter(
        (tab) => tab
      )([
        isManager ? null : {
          path: tabKeys.trainingPlansSettings.userTrainingPlan,
          title: 'My training plan',
          content: <UserTrainingPlan />
        },
        isBusinessAdmin ? {
          path: tabKeys.trainingPlansSettings.businessTrainingPlan,
          title: 'Company training plan',
          content: <BusinessTrainingPlan />
        } : null,
        (isTeamManager && !isBusinessAdmin) ? {
          path: tabKeys.trainingPlansSettings.teamTrainingPlans,
          title: isTeamManager.businessTeamName,
          content: <TeamTrainingPlan />
        } : null,
        (isBusinessAdmin || isTeamManager) ? {
          path: tabKeys.trainingPlansSettings.allTrainingPlans,
          title: isBusinessAdmin ? 'Users training plans' : 'Team member training plans',
          content: isBusinessAdmin ? <UsersTrainingPlans /> : <TeamMembersTrainingPlans teamId={isTeamManager.businessTeamId} />
        } : null,
        isBusinessAdmin ? {
          path: tabKeys.trainingPlansSettings.teamTrainingPlans,
          title: 'Team training plans',
          content: <TeamsTrainingPlans />
        } : null
      ])
    })
  )
)(AccountSettings);

export default AccountSettings;



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/index.js